/* eslint-disable jira/filename-case */
import React, { Component, type ComponentType, type ElementRef, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import type { UIAnalyticsEvent as AnalyticsEvent } from '@atlaskit/analytics-next';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { AnalyticsSource } from '@atlassian/jira-analytics-web-react/src/components/decorators.tsx';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { MODAL } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { CreateReleaseNotesPrompt } from './create-release-notes-prompt/CreateReleaseNotesPrompt.tsx';
import messages from './messages.tsx';

export type Props = {
	readonly unresolvedIssuesCount: number;
	readonly isSaving: boolean;
	readonly isInitiating: boolean;
	readonly isReleaseDisabled: boolean;
	readonly name: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly Content: ComponentType<Record<any, any>>;
	readonly onCancel: (arg1: SyntheticEvent<HTMLElement>) => void;
	readonly onRelease: (isCreateReleaseNotesOptedIn: boolean, arg1: AnalyticsEvent) => void;
	readonly intl: IntlShape;
	readonly shouldShowCreateReleaseNotesOption: boolean;
	readonly setRedirectToCreateReleaseNotesStatus: (arg: boolean) => void;
	readonly shouldRedirectToCreateReleaseNotes: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
export class ReleaseModal extends Component<Props> {
	// @ts-expect-error - TS2344 - Type '{ new (): HTMLElement; prototype: HTMLElement; }' does not satisfy the constraint 'keyof IntrinsicElements | ForwardRefExoticComponent<any> | (new (props: any) => Component<any, {}, any>) | ((props: any, context?: any) => ReactElement<...> | null)'.
	onButtonMounted = (node: ElementRef<typeof HTMLElement> | null): void => {
		this.button = node;
	};

	// focus needs to be set on button in order to hide date-picker
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onRelease = (...args: any[]): void => {
		if (this.button) {
			this.button.focus();
		}
		this.props.onRelease(this.props.shouldRedirectToCreateReleaseNotes, args[args.length - 1]);
	};

	// @ts-expect-error - TS2564 - Property 'button' has no initializer and is not definitely assigned in the constructor.
	button: HTMLElement | null;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any, jira/react-no-unneeded-arrow-function-properties
	renderFooter = (props: any): React.JSX.Element => {
		const {
			intl: { formatMessage },
			unresolvedIssuesCount,
			isSaving,
			onCancel,
			isReleaseDisabled,
			shouldRedirectToCreateReleaseNotes,
			setRedirectToCreateReleaseNotesStatus,
			shouldShowCreateReleaseNotesOption,
		} = this.props;

		return (
			<ModalFooter {...props}>
				<FooterContainer showReleaseNotesPrompt={shouldShowCreateReleaseNotesOption}>
					{shouldShowCreateReleaseNotesOption && (
						<CreateReleaseNotesPrompt
							onChange={setRedirectToCreateReleaseNotesStatus}
							value={shouldRedirectToCreateReleaseNotes}
						/>
					)}

					<ButtonsContainer>
						{isSaving && (
							<Box xcss={spinnerContainerStyles}>
								<Spinner size="small" />
							</Box>
						)}
						<Button
							onClick={this.onRelease}
							appearance={unresolvedIssuesCount ? 'warning' : 'primary'}
							type="submit"
							isDisabled={isReleaseDisabled}
							ref={this.onButtonMounted}
						>
							{formatMessage(messages.release)}
						</Button>
						<Button appearance="subtle-link" isDisabled={false} onClick={onCancel}>
							{formatMessage(messages.cancel)}
						</Button>
					</ButtonsContainer>
				</FooterContainer>
			</ModalFooter>
		);
	};

	render(): React.JSX.Element | null {
		const {
			Content,
			unresolvedIssuesCount,
			isInitiating,
			onCancel,
			intl: { formatMessage },
			name,
		} = this.props;
		if (isInitiating) {
			return null;
		}

		return (
			<ShortcutScope>
				<ModalDialog
					isOpen
					width="small"
					// @ts-expect-error - TS2769 - No overload matches this call.
					onClose={onCancel}
					testId="version-modals.release-version-modal.release-version-modal"
					messageId="version-modals.release-version-modal.modal-dialog.release-version-modal"
					messageType="transactional"
				>
					<ModalHeader hasCloseButton={fg('enghealth-41206-modal-header-close-button')}>
						{unresolvedIssuesCount ? (
							<ModalTitle appearance="warning">
								{formatMessage(messages.header, { name })}
							</ModalTitle>
						) : (
							<ModalTitle>{formatMessage(messages.header, { name })}</ModalTitle>
						)}
					</ModalHeader>
					<ModalBody>
						<Content />
					</ModalBody>

					{this.renderFooter(this.props)}
				</ModalDialog>
			</ShortcutScope>
		);
	}
}

export default AnalyticsSource(
	'releaseVersions',
	MODAL,
)(
	withFireUiAnalytics({
		onRelease: (isCreateReleaseNotesOptedIn: boolean) => ({
			name: 'release',
			isCreateReleaseNotesOptedIn,
		}),
		onCancel: 'cancel',
	})(injectIntl(ReleaseModal)),
);

const spinnerContainerStyles = xcss({
	marginRight: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonsContainer = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContainer = styled.div<{ showReleaseNotesPrompt: boolean }>({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ showReleaseNotesPrompt }) =>
		showReleaseNotesPrompt ? 'space-between' : 'flex-end',
	width: '100%',
});
